// Avatar grid for a single cell. PLAN.md §2.3, §7.3.

import { useMemo } from 'react'
import type { CellNonSpectator } from '@trading-grounds/shared'
import { Avatar } from '../Avatar.js'
import { useStore } from '../../store.js'

const SCALE_FLOOR = 0.36
function scaleFor(occupancy: number): number {
  return Math.max(SCALE_FLOOR, Math.min(1, 1 - Math.max(0, occupancy - 6) * 0.04))
}

export function CellCrowd({ cell }: { cell: CellNonSpectator }) {
  const players = useStore((s) => s.players)
  const tombstones = useStore((s) => s.tombstones)
  const self = useStore((s) => s.self)

  const list = useMemo(() => {
    const live: { id: string; seed: string; cumulative_pnl: number; status: string; ghost: boolean; isMe: boolean }[] = []
    for (const p of players.values()) {
      const localOptimisticCell = p.id === self?.player_id ? self.optimistic_target : null
      const renderCell = localOptimisticCell ?? p.cell
      const sourceCell = p.cell
      const isMe = p.id === self?.player_id

      // Hide self in source cell during optimistic move
      if (isMe && localOptimisticCell && sourceCell !== cell && renderCell !== cell) {
        continue
      }
      if (renderCell !== cell) continue

      live.push({
        id: p.id,
        seed: p.avatar_seed,
        cumulative_pnl: p.cumulative_pnl,
        status: p.status,
        ghost: isMe && localOptimisticCell === cell && sourceCell !== cell,
        isMe,
      })
    }
    return live
  }, [players, self, cell])

  const tombsForCell = useMemo(
    () => tombstones.filter((t) => t.cell === cell),
    [tombstones, cell],
  )

  const totalOccupancy = list.length + tombsForCell.length
  const scale = scaleFor(totalOccupancy)

  if (totalOccupancy === 0) {
    return <div className="min-h-7 flex items-center justify-center text-label text-grey/60">empty</div>
  }

  return (
    <div className="flex flex-wrap gap-1.5 items-end justify-center min-h-7">
      {list.map((p) => (
        <Avatar
          key={p.id}
          seed={p.seed}
          isMe={p.isMe}
          cumulativePnl={p.cumulative_pnl}
          deathPulse={p.status === 'death_watch'}
          ghost={p.ghost}
          scale={scale}
        />
      ))}
      {tombsForCell.map((t) => (
        <Avatar key={`t:${t.player_id}:${t.liquidated_tick}`} seed={t.avatar_seed} tombstone scale={scale} />
      ))}
    </div>
  )
}
